import * as React from 'react';
import { Icon, Text } from '@/hen-ui/atomics';
import { ExpertByLine } from '@/hen-ui/composites/ExpertByLine';
import { cn } from '@/hen-ui/utils';
import {
  expertQuoteVariants,
  type ExpertQuoteVariantProps,
} from './expert-quote.variants';
import { Flex } from '@/hen-ui/composites/Flex';

export interface ExpertQuoteProps extends ExpertQuoteVariantProps {
  /** Quote text content. */
  quote: string;
  /** Expert name for attribution. */
  expertName: string;
  /** Expert occupation/title for attribution. */
  expertRole: string;
  /** Optional avatar image URL. */
  expertAvatarSrc?: string;
  /** Optional avatar initials fallback when no image. */
  expertAvatarFallback?: string;
  /** Optional href for expert name (e.g. profile link). */
  expertHref?: string;
  /** Additional class name for the card container. */
  className?: string;
}

/**
 * ExpertQuote – Blockquote with quote icon, quote text, and Expert Byline attribution.
 * Uses semantic <blockquote> with <footer> for attribution. Styled with EDU Design System tokens.
 */
export const ExpertQuote = React.forwardRef<HTMLDivElement, ExpertQuoteProps>(
  (
    {
      quote,
      expertName,
      expertRole,
      expertAvatarSrc,
      expertAvatarFallback,
      expertHref,
      className,
      ...variantProps
    },
    ref,
  ) => (
    <Flex
      direction='column'
      ref={ref}
      className={cn(expertQuoteVariants(variantProps), className)}
    >
      <Icon
        icon='message-square-quote'
        color='neutral'
        strokeWidth={0.75}
        className='shrink-0'
        size='3xl'
        aria-hidden
      />
      {/* TODO: Replace blockquote and footer with a composite component */}
      <blockquote cite={expertHref} className='mt-0'>
        <Text variant='b4' color='default' tag='p' className='min-w-0'>
          {quote}
        </Text>
        <footer className='mt-l1'>
          <ExpertByLine
            name={expertName}
            role={expertRole}
            avatarSrc={expertAvatarSrc}
            avatarFallback={expertAvatarFallback}
            href={expertHref}
          />
        </footer>
      </blockquote>
    </Flex>
  ),
);

ExpertQuote.displayName = 'ExpertQuote';
